import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import {
  MegaphoneIcon,
  PlusIcon,
  PaperAirplaneIcon,
  CalendarIcon,
  ClockIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ChartBarIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'
import {
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  sendAnnouncement,
  cancelAnnouncement,
  getAnnouncementStats,
  getAudiencePreview,
  type Announcement,
  type CreateAnnouncementRequest,
  type AnnouncementStatus,
  type AnnouncementPriority,
  type TargetAudience
} from '@/api/communicationApi'
import { AudienceSelector } from '@/components/communication'
import { Button } from '@/components/ui/Button'
import { format, parseISO } from 'date-fns'

const announcementSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  content: z.string().min(10, 'Content must be at least 10 characters'),
  priority: z.enum(['low', 'normal', 'high', 'urgent']),
  channels: z.object({
    inApp: z.boolean(),
    email: z.boolean(),
    sms: z.boolean()
  }).refine(data => data.inApp || data.email || data.sms, {
    message: 'At least one channel must be selected'
  }),
  scheduledAt: z.string().optional(),
  acknowledgmentRequired: z.boolean()
})

type AnnouncementFormData = z.infer<typeof announcementSchema>

const statusColors: Record<AnnouncementStatus, { bg: string; text: string; dot: string }> = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-500' },
  scheduled: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  sending: { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  sent: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  failed: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400' }
}

const priorityColors: Record<AnnouncementPriority, { bg: string; text: string }> = {
  low: { bg: 'bg-gray-100', text: 'text-gray-600' },
  normal: { bg: 'bg-blue-100', text: 'text-blue-600' },
  high: { bg: 'bg-orange-100', text: 'text-orange-600' },
  urgent: { bg: 'bg-red-100', text: 'text-red-600' }
}

export default function AnnouncementsPage() {
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null)
  const [viewingAnnouncement, setViewingAnnouncement] = useState<Announcement | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<AnnouncementStatus | ''>('')
  const [priorityFilter, setPriorityFilter] = useState<AnnouncementPriority | ''>('')
  const [currentPage, setCurrentPage] = useState(1)
  const [targetAudience, setTargetAudience] = useState<TargetAudience>({
    type: 'all_school'
  })

  // Fetch announcements
  const { data: announcementsData, isLoading } = useQuery({
    queryKey: ['announcements', currentPage, searchQuery, statusFilter, priorityFilter],
    queryFn: () => getAnnouncements({
      page: currentPage,
      limit: 10,
      search: searchQuery || undefined,
      status: statusFilter || undefined,
      priority: priorityFilter || undefined,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    })
  })

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['announcement-stats'],
    queryFn: () => getAnnouncementStats()
  })

  // Audience preview
  const { data: audiencePreview, isLoading: previewLoading } = useQuery({
    queryKey: ['audience-preview', targetAudience],
    queryFn: () => getAudiencePreview(targetAudience),
    enabled: isModalOpen
  })

  // Form
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    trigger,
    getValues,
    formState: { errors, isSubmitting }
  } = useForm<AnnouncementFormData>({
    resolver: zodResolver(announcementSchema),
    defaultValues: {
      title: '',
      content: '',
      priority: 'normal',
      channels: { inApp: true, email: false, sms: false },
      acknowledgmentRequired: false
    }
  })

  const scheduledAt = watch('scheduledAt')

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateAnnouncementRequest) => createAnnouncement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] })
      queryClient.invalidateQueries({ queryKey: ['announcement-stats'] })
      toast.success('Announcement created successfully')
      closeModal()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create announcement')
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => updateAnnouncement(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] })
      toast.success('Announcement updated successfully')
      closeModal()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update announcement')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAnnouncement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] })
      queryClient.invalidateQueries({ queryKey: ['announcement-stats'] })
      toast.success('Announcement deleted successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete announcement')
    }
  })

  const sendMutation = useMutation({
    mutationFn: (id: string) => sendAnnouncement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] })
      queryClient.invalidateQueries({ queryKey: ['announcement-stats'] })
      toast.success('Announcement sent successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to send announcement')
    }
  })

  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelAnnouncement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] })
      toast.success('Announcement cancelled')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to cancel announcement')
    }
  })

  const openCreateModal = () => {
    setEditingAnnouncement(null)
    setTargetAudience({ type: 'all_school' })
    reset({
      title: '',
      content: '',
      priority: 'normal',
      channels: { inApp: true, email: false, sms: false },
      acknowledgmentRequired: false
    })
    setIsModalOpen(true)
  }

  const openEditModal = (announcement: Announcement) => {
    setEditingAnnouncement(announcement)
    setTargetAudience(announcement.targetAudience)
    reset({
      title: announcement.title,
      content: announcement.content,
      priority: announcement.priority,
      channels: announcement.channels,
      scheduledAt: announcement.scheduledAt || undefined,
      acknowledgmentRequired: announcement.acknowledgmentRequired
    })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingAnnouncement(null)
    reset()
  }

  const onSubmit = async (data: AnnouncementFormData) => {
    const payload: CreateAnnouncementRequest = {
      title: data.title,
      content: data.content,
      priority: data.priority,
      channels: {
        inApp: data.channels.inApp ?? true,
        email: data.channels.email ?? false,
        sms: data.channels.sms ?? false
      },
      acknowledgmentRequired: data.acknowledgmentRequired,
      targetAudience,
      scheduledAt: data.scheduledAt || undefined
    }

    if (editingAnnouncement) {
      await updateMutation.mutateAsync({ id: editingAnnouncement.id, data: payload })
    } else {
      await createMutation.mutateAsync(payload)
    }
  }

  const handleSendNow = async (id: string) => {
    if (confirm('Are you sure you want to send this announcement now?')) {
      await sendMutation.mutateAsync(id)
    }
  }

  const handleCancel = async (id: string) => {
    if (confirm('Are you sure you want to cancel this scheduled announcement?')) {
      await cancelMutation.mutateAsync(id)
    }
  }

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this announcement? This action cannot be undone.')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  const announcements = announcementsData?.announcements || []
  const pagination = announcementsData?.pagination

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Announcements</h1>
          <p className="text-sm text-gray-500 mt-1">
            Create and manage school-wide announcements
          </p>
        </div>
        <Button onClick={openCreateModal} className="mt-4 sm:mt-0">
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Announcement
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MegaphoneIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalAnnouncements || 0}</p>
              <p className="text-xs text-gray-500">Total Announcements</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats?.sent || 0}</p>
              <p className="text-xs text-gray-500">Sent</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ClockIcon className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats?.scheduled || 0}</p>
              <p className="text-xs text-gray-500">Scheduled</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <DocumentTextIcon className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats?.drafts || 0}</p>
              <p className="text-xs text-gray-500">Drafts</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search announcements..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as AnnouncementStatus | '')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as AnnouncementPriority | '')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Priority</option>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>
      </div>

      {/* Announcements List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : announcements.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <MegaphoneIcon className="h-12 w-12 text-gray-300" />
            <p className="mt-4 text-gray-500">No announcements found</p>
            <Button onClick={openCreateModal} variant="outline" className="mt-4">
              <PlusIcon className="h-4 w-4 mr-2" />
              Create your first announcement
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {announcements.map((announcement) => {
              const statusColor = statusColors[announcement.status]
              const priorityColor = priorityColors[announcement.priority]

              return (
                <motion.div
                  key={announcement.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-1">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {announcement.title}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor.bg} ${statusColor.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${statusColor.dot}`} />
                          {announcement.status}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${priorityColor.bg} ${priorityColor.text}`}>
                          {announcement.priority}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                        {announcement.content}
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-gray-400">
                        <span className="flex items-center">
                          <UserGroupIcon className="h-4 w-4 mr-1" />
                          {announcement.targetAudience.type.replace('_', ' ')}
                        </span>
                        {announcement.scheduledAt && (
                          <span className="flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {format(parseISO(announcement.scheduledAt), 'MMM d, yyyy HH:mm')}
                          </span>
                        )}
                        <span className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {format(parseISO(announcement.createdAt), 'MMM d, yyyy')}
                        </span>
                        {announcement.deliveryStats && (
                          <span className="flex items-center">
                            <ChartBarIcon className="h-4 w-4 mr-1" />
                            {announcement.deliveryStats.totalRecipients} recipients
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      {announcement.status === 'draft' && (
                        <>
                          <button
                            onClick={() => handleSendNow(announcement.id)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Send Now"
                          >
                            <PaperAirplaneIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(announcement)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      {announcement.status === 'scheduled' && (
                        <button
                          onClick={() => handleCancel(announcement.id)}
                          className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                          title="Cancel"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setViewingAnnouncement(announcement)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      {(announcement.status === 'draft' || announcement.status === 'cancelled') && (
                        <button
                          onClick={() => handleDelete(announcement.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {(currentPage - 1) * pagination.limit + 1} to{' '}
              {Math.min(currentPage * pagination.limit, pagination.totalAnnouncements)} of{' '}
              {pagination.totalAnnouncements} announcements
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={!pagination.hasPrevPage}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50"
                onClick={closeModal}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              >
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title
                    </label>
                    <input
                      {...register('title')}
                      type="text"
                      placeholder="Enter announcement title"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                    )}
                  </div>

                  {/* Content */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Content
                    </label>
                    <textarea
                      {...register('content')}
                      rows={4}
                      placeholder="Write your announcement message..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    {errors.content && (
                      <p className="mt-1 text-sm text-red-600">{errors.content.message}</p>
                    )}
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      {...register('priority')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  {/* Target Audience */}
                  <AudienceSelector
                    value={targetAudience}
                    onChange={setTargetAudience}
                    showPreview={true}
                    previewData={audiencePreview}
                  />

                  {/* Delivery Channels */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Delivery Channels
                    </label>
                    <div className="flex flex-wrap gap-4">
                      <Controller
                        name="channels.inApp"
                        control={control}
                        render={({ field }) => (
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={field.value}
                              onChange={field.onChange}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700">In-App</span>
                          </label>
                        )}
                      />
                      <Controller
                        name="channels.email"
                        control={control}
                        render={({ field }) => (
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={field.value}
                              onChange={field.onChange}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700">Email</span>
                          </label>
                        )}
                      />
                      <Controller
                        name="channels.sms"
                        control={control}
                        render={({ field }) => (
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={field.value}
                              onChange={field.onChange}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700">SMS</span>
                          </label>
                        )}
                      />
                    </div>
                    {errors.channels && (
                      <p className="mt-1 text-sm text-red-600">{errors.channels.message}</p>
                    )}
                  </div>

                  {/* Schedule */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Schedule (Optional)
                    </label>
                    <input
                      {...register('scheduledAt')}
                      type="datetime-local"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Leave empty to save as draft or send immediately
                    </p>
                  </div>

                  {/* Acknowledgment */}
                  <div>
                    <Controller
                      name="acknowledgmentRequired"
                      control={control}
                      render={({ field }) => (
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700">
                            Require acknowledgment from recipients
                          </span>
                        </label>
                      )}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                    <Button type="button" variant="secondary" onClick={closeModal}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      loading={isSubmitting || createMutation.isPending || updateMutation.isPending}
                    >
                      {scheduledAt ? 'Schedule' : editingAnnouncement ? 'Update' : 'Save as Draft'}
                    </Button>
                    {!scheduledAt && !editingAnnouncement && (
                      <Button
                        type="button"
                        onClick={async () => {
                          const isValid = await trigger()
                          if (!isValid) return
                          const data = getValues()
                          const payload: CreateAnnouncementRequest = {
                            title: data.title,
                            content: data.content,
                            priority: data.priority,
                            channels: {
                              inApp: data.channels.inApp ?? true,
                              email: data.channels.email ?? false,
                              sms: data.channels.sms ?? false
                            },
                            acknowledgmentRequired: data.acknowledgmentRequired,
                            targetAudience
                          }
                          const created = await createMutation.mutateAsync(payload)
                          if (created) {
                            await sendMutation.mutateAsync(created.id)
                          }
                        }}
                        loading={sendMutation.isPending}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                        Send Now
                      </Button>
                    )}
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* View Details Modal */}
      <AnimatePresence>
        {viewingAnnouncement && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50"
                onClick={() => setViewingAnnouncement(null)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white rounded-xl shadow-xl w-full max-w-lg"
              >
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Announcement Details</h2>
                  <button
                    onClick={() => setViewingAnnouncement(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{viewingAnnouncement.title}</h3>
                    <div className="flex items-center space-x-2 mt-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[viewingAnnouncement.status].bg} ${statusColors[viewingAnnouncement.status].text}`}>
                        {viewingAnnouncement.status}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${priorityColors[viewingAnnouncement.priority].bg} ${priorityColors[viewingAnnouncement.priority].text}`}>
                        {viewingAnnouncement.priority}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-600">{viewingAnnouncement.content}</p>

                  {viewingAnnouncement.deliveryStats && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Delivery Stats</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Total Recipients:</span>{' '}
                          <span className="font-medium">{viewingAnnouncement.deliveryStats.totalRecipients}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">In-App Delivered:</span>{' '}
                          <span className="font-medium">{viewingAnnouncement.deliveryStats.inAppDelivered}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Email Delivered:</span>{' '}
                          <span className="font-medium">{viewingAnnouncement.deliveryStats.emailDelivered}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">SMS Delivered:</span>{' '}
                          <span className="font-medium">{viewingAnnouncement.deliveryStats.smsDelivered}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Read:</span>{' '}
                          <span className="font-medium">{viewingAnnouncement.deliveryStats.inAppRead}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Acknowledged:</span>{' '}
                          <span className="font-medium">{viewingAnnouncement.deliveryStats.acknowledged}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="text-xs text-gray-400 space-y-1">
                    <p>Created: {format(parseISO(viewingAnnouncement.createdAt), 'PPpp')}</p>
                    {viewingAnnouncement.sentAt && (
                      <p>Sent: {format(parseISO(viewingAnnouncement.sentAt), 'PPpp')}</p>
                    )}
                    {viewingAnnouncement.createdBy && (
                      <p>By: {viewingAnnouncement.createdBy.firstName} {viewingAnnouncement.createdBy.lastName}</p>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
